import React from 'react';
import { useOnboardingMetrics } from '../hooks/useOnboardingMetrics';

const STEP_NAMES: Record<number, string> = {
  0: 'Email Submitted',
  1: 'Name Entry',
  2: 'Date of Birth',
  3: 'Medication Selection',
  4: 'Dosage Entry',
  5: 'Quantity Entry',
  6: 'Day Supply',
  7: 'Last Pickup Date',
  8: 'Pharmacy Selection',
  9: 'Prescriber Info',
  10: 'Contact Info'
};

export default function OnboardingFunnel() {
  const { metrics, loading } = useOnboardingMetrics();

  if (loading) {
    return (
      <div className="bg-pure-white p-6 rounded-md shadow-card">
        <h3 className="text-section-header text-gray-900 font-inter mb-4">Onboarding Funnel</h3>
        <p className="text-supporting text-text-secondary">Loading...</p>
      </div>
    );
  }

  // Find the step with the most stuck users
  const stuckSteps = Object.entries(metrics.stuckAtStep)
    .map(([step, count]) => ({ step: parseInt(step), count: count as number, name: STEP_NAMES[parseInt(step)] }))
    .sort((a, b) => b.count - a.count);

  const topStuckStep = stuckSteps[0];

  return (
    <div className="bg-pure-white p-6 rounded-md shadow-card">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-section-header text-gray-900 font-inter">Onboarding Funnel</h3>
        <div className="text-supporting text-text-secondary">
          <span className={`font-semibold ${metrics.completionRate >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
            {metrics.completionRate}%
          </span> completion rate
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-500 uppercase mb-1">Total Signups</p>
          <p className="text-2xl font-semibold text-gray-900">{metrics.totalSignups}</p>
        </div>
        <div className="p-4 bg-green-50 rounded-md">
          <p className="text-xs text-gray-500 uppercase mb-1">Completed</p>
          <p className="text-2xl font-semibold text-green-600">{metrics.completedSignups}</p>
        </div>
        <div className="p-4 bg-orange-50 rounded-md">
          <p className="text-xs text-gray-500 uppercase mb-1">Incomplete</p>
          <p className="text-2xl font-semibold text-orange-600">{metrics.incompleteSignups}</p>
        </div>
        <div className="p-4 bg-blue-50 rounded-md">
          <p className="text-xs text-gray-500 uppercase mb-1">Avg Time</p>
          <p className="text-2xl font-semibold text-blue-600">
            {metrics.avgTimeToComplete}h
          </p>
        </div>
      </div>

      {/* Drop-off Points */}
      {stuckSteps.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Where Users Get Stuck</h4>
          <div className="space-y-2">
            {stuckSteps.slice(0, 5).map(({ step, count, name }) => (
              <div key={step} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div>
                  <p className="text-sm font-medium text-gray-900">{name}</p>
                  <p className="text-xs text-gray-500">Step {step} of 10</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-orange-600">{count}</span>
                  <span className="text-xs text-gray-500">students</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert if many users are stuck */}
      {topStuckStep && (topStuckStep.count as number) >= 5 && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ High drop-off alert:</strong> {topStuckStep.count} students stuck at "{topStuckStep.name}".
            Consider simplifying this step or adding help text.
          </p>
        </div>
      )}

      {/* Empty State */}
      {stuckSteps.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No incomplete signups yet</p>
        </div>
      )}
    </div>
  );
}
